/**
 * Cache Management Utilities
 * Provides functions to handle cache busting and force browser refresh
 */

export interface CacheConfig {
  version?: string;
  timestamp?: boolean;
  forceRefresh?: boolean;
}

export class CacheManager {
  private static readonly STORAGE_KEY = "app_cache_version";
  private static readonly VERSION_PARAM = "v";

  /**
   * Get current app version from various sources
   */
  static getCurrentVersion(): string {
    // Try to get version from build-time define
    const buildVersion = (globalThis as any).__APP_VERSION__;
    if (buildVersion) return buildVersion.toString();

    // Fallback to a static version string (not a timestamp that changes each time)
    // This should only be used in development
    return "dev-1.0.0";
  }

  /**
   * Add cache busting parameters to a URL
   */
  static addCacheBuster(url: string, config: CacheConfig = {}): string {
    const urlObj = new URL(url, window.location.origin);

    if (config.timestamp !== false) {
      urlObj.searchParams.set("_t", Date.now().toString());
    }

    if (config.version) {
      urlObj.searchParams.set(this.VERSION_PARAM, config.version);
    } else {
      urlObj.searchParams.set(this.VERSION_PARAM, this.getCurrentVersion());
    }

    return urlObj.toString();
  }

  /**
   * Force browser to reload with cache busting
   */
  static forceReload(hardReload = false): void {
    if (hardReload) {
      // Hard reload - bypasses cache completely
      window.location.reload();
    } else {
      // Soft reload with cache busting
      const newUrl = this.addCacheBuster(window.location.href);
      window.location.href = newUrl;
    }
  }

  /**
   * Clear all browser caches
   */
  static async clearAllCaches(): Promise<void> {
    // Clear localStorage version
    localStorage.removeItem(this.STORAGE_KEY);

    // Clear sessionStorage
    sessionStorage.clear();

    // Clear service worker caches
    if ("serviceWorker" in navigator) {
      const registrations = await navigator.serviceWorker.getRegistrations();
      for (const registration of registrations) {
        await registration.update();
      }
    }

    // Clear Cache API if available
    if ("caches" in window) {
      const cacheNames = await caches.keys();
      await Promise.all(
        cacheNames.map((cacheName) => caches.delete(cacheName)),
      );
    }
  }

  /**
   * Check if app version has changed and force refresh if needed
   */
  static checkVersionAndRefresh(): boolean {
    const currentVersion = this.getCurrentVersion();
    const storedVersion = localStorage.getItem(this.STORAGE_KEY);

    // Only reload if there's a stored version AND it's different from current
    // AND we haven't just reloaded (prevent infinite loops)
    const lastReloadTime = sessionStorage.getItem("last_reload_time");
    const now = Date.now();

    if (storedVersion && storedVersion !== currentVersion) {
      // Prevent reload if we just reloaded less than 5 seconds ago
      if (lastReloadTime && now - parseInt(lastReloadTime) < 5000) {
        console.warn(
          "Prevented infinite reload loop - version changed too quickly",
        );
        localStorage.setItem(this.STORAGE_KEY, currentVersion);
        return false;
      }

      localStorage.setItem(this.STORAGE_KEY, currentVersion);
      sessionStorage.setItem("last_reload_time", now.toString());
      this.forceReload(true);
      return true;
    }

    if (!storedVersion) {
      localStorage.setItem(this.STORAGE_KEY, currentVersion);
    }

    return false;
  }

  /**
   * Create a cache-busted asset URL
   */
  static getAssetUrl(assetPath: string): string {
    // Remove leading slash if present
    const cleanPath = assetPath.startsWith("/")
      ? assetPath.slice(1)
      : assetPath;

    // For development, add cache buster
    if (import.meta.env.DEV) {
      return this.addCacheBuster(`/${cleanPath}`);
    }

    // For production, Vite handles hashing automatically
    return `/${cleanPath}`;
  }

  /**
   * Initialize cache management
   */
  static init(): void {
    // Set initial version if not exists
    const currentVersion = this.getCurrentVersion();
    if (!localStorage.getItem(this.STORAGE_KEY)) {
      localStorage.setItem(this.STORAGE_KEY, currentVersion);
    }

    // Disabled automatic version checks on tab visibility changes
    // to prevent unexpected reloads during user sessions
    // If you need this feature, uncomment the code below:
    /*
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        // Check for updates when user returns to tab
        setTimeout(() => this.checkVersionAndRefresh(), 1000);
      }
    });
    */
  }
}

// Auto-initialize is disabled to prevent issues
// Call CacheManager.init() manually if needed
// CacheManager.init();

export default CacheManager;
